import React, { useEffect, useRef, useState } from 'react';
import { SignedIn, SignedOut, UserButton, SignInButton, useUser, useClerk } from '@clerk/clerk-react';
// CB:BEGIN header-mobile-import
import { useDevice } from '../hooks/useDevice';
// CB:END header-mobile-import
import ResidenceTab from './UserProfileTabs/ResidenceTab';
import PaymentsTab from './UserProfileTabs/PaymentsTab';
import CommunicationPreferenceTab from './UserProfileTabs/CommunicationPreferenceTab';

interface SharedHeaderProps {
  onNavigate: (screen: string) => void;
  contractorNavButtons?: React.ReactNode;
  upgradePlanButton?: React.ReactNode;
  // Mobile hamburger menu (for mobile landing header)
  mobileMenuEnabled?: boolean;
  mobileMenuOnly?: boolean; // hide inline login/cta on mobile landing
  onMobileFaqClick?: () => void; // allows FAQs to scroll on landing
  theme?: 'dark' | 'light';
}

const SharedHeaderHomeowner: React.FC<SharedHeaderProps> = ({
  onNavigate,
  contractorNavButtons,
  upgradePlanButton,
  mobileMenuEnabled,
  mobileMenuOnly,
  onMobileFaqClick,
  theme = 'dark',
}) => {
  const { user } = useUser();
  useClerk();
  // CB:BEGIN header-mobile-sizing
  const { isMobileLayout } = useDevice();
  const buttonFont = isMobileLayout ? '11px' : '14px';
  const buttonPad = isMobileLayout ? '6px 10px' : '8px 16px';
  const headerPadding = isMobileLayout ? '10px 14px' : '20px 30px';
  const headerGap = isMobileLayout ? 10 : 32;
  const logoFont = isMobileLayout ? 18 : 24;
  // CB:END header-mobile-sizing
  const [menuOpen, setMenuOpen] = useState(false);
  const menuWrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (menuWrapRef.current && !menuWrapRef.current.contains(target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown as any);
    };
  }, [menuOpen]);
  
  // CB:BEGIN detect-user-type-for-routing
  // Get usertype from metadata or localStorage cache to route Dashboard correctly
  const meta = (user?.publicMetadata as any) || {};
  let usertype = (meta.usertype || meta.userType || '').toString().toLowerCase();
  
  // If usertype is not found in metadata, check localStorage cache
  if (!usertype || usertype === '') {
    const cached = localStorage.getItem("cb.usertype");
    if (cached === "contractor" || cached === "homeowner" || cached === "admin") {
      usertype = cached;
    }
  }
  
  // Get the correct dashboard route based on user type
  const getDashboardRoute = (): string => {
    if (usertype === 'contractor') {
      return 'contractor-dashboard';
    } else if (usertype === 'admin') {
      return 'admin-dashboard';
    }
    // Default to customer-dashboard for homeowners
    return 'customer-dashboard';
  };
  // CB:END detect-user-type-for-routing
  const brandColor = '#123c3b'; // green header for non-mobile-landing
  const bgColor = theme === 'light' ? '#ffffff' : brandColor;
  const textColor = theme === 'light' ? brandColor : '#ffffff';

  return (
          <div className="header sticky top-0 z-40" style={{ background: bgColor, color: textColor, padding: headerPadding, display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 40, WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation', boxShadow: '0 6px 16px rgba(0,0,0,0.16)' }}>
      {/* CB:BEGIN safe-area-top-spacer */}
      <div className="sr-only" style={{ paddingTop: 'env(safe-area-inset-top)' }} />
      {/* CB:END safe-area-top-spacer */}
      <div style={{ display: 'flex', alignItems: 'center', gap: headerGap }}>
        <div className="logo" style={{ fontSize: logoFont, fontWeight: 700, letterSpacing: -0.5, cursor: 'pointer', lineHeight: 1, color: textColor }} onClick={() => onNavigate('landing')}>CoreBids</div>
        
        
        {/* Contractor Navigation Buttons - only shown when provided */}
        {contractorNavButtons && (
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            {contractorNavButtons}
          </div>
        )}
      </div>
      
      <div className="nav-buttons" style={{ display: 'flex', gap: isMobileLayout ? 6 : 15, alignItems: 'center', flexWrap: 'nowrap', justifyContent: 'flex-end', minWidth: 0 }}>
        {/* Mobile hamburger menu on the right */}
        {isMobileLayout && mobileMenuEnabled && (
          <div ref={menuWrapRef} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <button
              type="button"
              aria-label="Open menu"
              onClick={() => setMenuOpen((v) => !v)}
              style={{
                width: 38,
                height: 38,
                borderRadius: 999,
                border: theme === 'light' ? '1px solid rgba(23,53,54,0.2)' : '1px solid rgba(255,255,255,0.25)',
                background: theme === 'light' ? 'rgba(23,53,54,0.06)' : 'rgba(255,255,255,0.10)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 6px 14px rgba(0,0,0,0.18)',
              }}
            >
              <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ width: 16, height: 2, background: theme === 'light' ? '#173536' : 'rgba(255,255,255,0.92)', borderRadius: 2 }} />
                <span style={{ width: 16, height: 2, background: theme === 'light' ? '#173536' : 'rgba(255,255,255,0.92)', borderRadius: 2 }} />
                <span style={{ width: 16, height: 2, background: theme === 'light' ? '#173536' : 'rgba(255,255,255,0.92)', borderRadius: 2 }} />
              </span>
            </button>

            {menuOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 10px)',
                  right: 0,
                  width: 220,
                  background: '#ffffff',
                  borderRadius: 14,
                  border: '1px solid rgba(15, 23, 42, 0.10)',
                  boxShadow: '0 18px 50px rgba(0,0,0,0.18)',
                  overflow: 'hidden',
                  zIndex: 9999,
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    if (onMobileFaqClick) onMobileFaqClick();
                    else onNavigate('faq');
                  }}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '14px 14px',
                    fontSize: 16,
                    fontWeight: 600,
                    color: '#0f172a',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  FAQs
                </button>

                <div style={{ height: 1, background: 'rgba(15, 23, 42, 0.08)' }} />

                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    onNavigate('contractor-landing');
                  }}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '14px 14px',
                    fontSize: 16,
                    fontWeight: 600,
                    color: '#0f172a',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  Join as a contractor
                  <div style={{ fontSize: 12, fontWeight: 500, color: '#475569', marginTop: 2 }}>
                    Create your profile and bid
                  </div>
                </button>

                <div style={{ height: 1, background: 'rgba(15, 23, 42, 0.08)' }} />

                <SignInButton mode="modal">
                  <button
                    type="button"
                    onClick={() => setMenuOpen(false)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: '14px 14px',
                      fontSize: 16,
                      fontWeight: 600,
                      color: '#0f172a',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    Login
                  </button>
                </SignInButton>
              </div>
            )}
          </div>
        )}

        {/* Contractor CTA removed on mobile landing */}
        <SignedIn>
          {upgradePlanButton && (
            <div style={{ marginRight: 15 }}>
              {upgradePlanButton}
            </div>
          )}
          <button
            onClick={() => onNavigate(getDashboardRoute())}
            style={{
              fontSize: 14, padding: '7px 14px', marginRight: 8,
              color: 'white', background: '#6b7280', border: 'none',
              fontWeight: 500, borderRadius: 6, cursor: 'pointer',
              transition: 'background 0.15s', whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#4b5563'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#6b7280'; }}
          >
            Dashboard
          </button>
          <span style={{ color: 'rgba(255,255,255,0.8)' }}>Welcome, {user?.firstName || user?.fullName?.split(' ')[0] || 'User'}</span>
          <UserButton afterSignOutUrl="/">
            <UserButton.UserProfilePage label="Residence" url="residence" labelIcon={<span>🏠</span>}>
              <ResidenceTab />
            </UserButton.UserProfilePage>
            <UserButton.UserProfilePage label="Payments" url="payments" labelIcon={<span>💳</span>}>
              <PaymentsTab />
            </UserButton.UserProfilePage>
            <UserButton.UserProfilePage label="Communication" url="communication-preference" labelIcon={<span>💬</span>}>
              <CommunicationPreferenceTab />
            </UserButton.UserProfilePage>
          </UserButton>
        </SignedIn>
        <SignedOut>
          {!(isMobileLayout && mobileMenuEnabled) && !mobileMenuOnly && (
            <SignInButton mode="modal">
              <button
                style={{
                  fontSize: buttonFont, padding: buttonPad,
                  color: 'white', background: '#6b7280', border: 'none',
                  fontWeight: 500, borderRadius: 6, cursor: 'pointer',
                  transition: 'background 0.15s', whiteSpace: 'nowrap',
                  display: 'inline-flex', alignItems: 'center', lineHeight: '1',
                  minWidth: isMobileLayout ? 64 : 0,
                  minHeight: isMobileLayout ? 32 : undefined,
                  flexShrink: 0,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#4b5563'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#6b7280'; }}
              >
                Login
              </button>
            </SignInButton>
          )}
        </SignedOut>
      </div>
    </div>
  );
};

export default SharedHeaderHomeowner; 